import React, { useState, useMemo, useEffect, useRef } from "react";
import tstyles from "../teacher/styles/TeacherResources.module.scss";
import nstyles from "../teacher/styles/noticeboard.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { profileDetails } from "../../../Redux/profile/profile.action";
import { Loader } from "lucide-react";
import ToastMessage from "../../../utils/toasts/toastMessage";
import { noticeList } from "../../../Redux/Teacher_Panel/notice_board/notice.action";
import { apiRequest } from "../../../api/apiRequest";
import { BASE_IMAGE_URL, GET_PDF_URL } from "../../../utils/api";

const CARDS_PER_PAGE = 4;

const HEADING_MIN = 3;
const HEADING_MAX = 150;
const DESC_MIN = 10;
const DESC_MAX = 1000;

const MultiSelectDropdown = ({
  id,
  label,
  options,
  selected,
  onChange,
  disabled,
  placeholder = "Select",
  hasError,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const displayLabel =
    selected.length === 0
      ? placeholder
      : selected.length <= 2
        ? selected.join(", ")
        : `${selected.length} selected`;

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        id={id}
        className={nstyles.formSelect}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          textAlign: "left",
          cursor: disabled ? "not-allowed" : "pointer",
          border: hasError ? "1px solid #e11d48" : undefined,
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={hasError || undefined}
      >
        <span>{displayLabel}</span>
        <span aria-hidden="true" style={{ marginLeft: 8 }}>
          {open ? "▲" : "▼"}
        </span>
      </button>

      {open && !disabled && (
        <div
          role="listbox"
          aria-label={label}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 20,
            background: "#fff",
            border: "1px solid #d0d5dd",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            maxHeight: 240,
            overflowY: "auto",
            padding: "4px 0",
          }}
        >
          {options.length === 0 ? (
            <div style={{ padding: "8px 12px", color: "#888", fontSize: 14 }}>
              No options
            </div>
          ) : (
            options.map((opt) => {
              const checked = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleOption(opt.value)}
                  />
                  {opt.label}
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const FieldError = ({ message }) =>
  message ? (
    <p
      role="alert"
      style={{
        color: "#e11d48",
        fontSize: 12,
        marginTop: 4,
        marginBottom: 0,
      }}
    >
      {message}
    </p>
  ) : null;

const NoticeCard = ({ id, title, desc, date, meta, image, eventDate }) => {
  return (
    <article className={nstyles.noticeCard} role="article" aria-label={title}>
      <div className={nstyles.cardHeader}>
        <div>
          {meta && <p className={nstyles.cardMeta}>{meta}</p>}
           <h3 className={nstyles.cardTitle}>Title: {title}</h3>
             <span className={nstyles.cardDate}>Date: {eventDate}</span>
        </div>

        {image ? (
          <div
            // className={nstyles.badge}
            aria-hidden="true"
            style={{
              width: 66,
              height: 66,
              flex: "0 0 66px",
              borderRadius: 10,
              overflow: "hidden",
              background: "#f1f1f1",
              padding: 0,
            }}
            onClick={() => {
              const imageUrl = `${BASE_IMAGE_URL}/data/uploads/announcements/${image}`;
              window.open(imageUrl, "_blank", "noopener,noreferrer");
            }}
          >
            <img
              src={`${BASE_IMAGE_URL}/data/uploads/announcements/${image}`}
              alt=""
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        ) : (
          <div className={nstyles.badge} aria-hidden="true" />
        )}
      </div>

      <p className={nstyles.cardDesc}>{desc}</p>

      <div className={nstyles.cardFooter}>
        <span className={nstyles.cardDate}>Published: {date}</span>
      </div>
    </article>
  );
};

const initialNotices = [];

function getPageRange(current, total, siblingCount = 1) {
  const totalNumbers = siblingCount * 2 + 5;

  if (total <= totalNumbers) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(current - siblingCount, 1);
  const rightSibling = Math.min(current + siblingCount, total);

  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < total - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftRange = Array.from(
      { length: 3 + siblingCount * 2 },
      (_, i) => i + 1,
    );
    return [...leftRange, "...", total];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightRange = Array.from(
      { length: 3 + siblingCount * 2 },
      (_, i) => total - (3 + siblingCount * 2) + i + 1,
    );
    return [1, "...", ...rightRange];
  }

  const middleRange = Array.from(
    { length: rightSibling - leftSibling + 1 },
    (_, i) => leftSibling + i,
  );
  return [1, "...", ...middleRange, "...", total];
}

export default function NoticeBoardStudent() {
  const dispatch = useDispatch();
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [toastState, setToastState] = useState({
    show: false,
    message: "",
    type: "",
  });

  const [form, setForm] = useState({
    classDivision: [],
    subject: [],
    heading: "",
    desc: "",
  });
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    classDivision: "",
    subject: "",
    heading: "",
    desc: "",
  });

  const NoticeListStore = useSelector(
    (state) => state?.NoticeTeacherManager?.notice?.Noticeboard,
  );

  const teacherDetails = useSelector(
    (state) => state?.ProfileManager?.user_details,
  );

  useEffect(() => {
    dispatch(profileDetails());
    dispatch(noticeList());
  }, [dispatch]);

  const notices = useMemo(() => {
    const reverseArray = NoticeListStore?.reverse();
    const raw = reverseArray || [];
    return raw.map((n) => ({
      id: n.id,
      subject: n.subject,
      title: n.title,
      desc: n.description,
      className: n.class_name,
      division: n.division,
      eventDate: n.event_date ?   new Date(n.event_date).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }) : "",
      date: n.created_at
        ? new Date(n.created_at).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "",
      image: n.image,
    }));
  }, [NoticeListStore]);

  const classAssignments = useMemo(
    () => teacherDetails?.classes || [],
    [teacherDetails],
  );

  const CLASS_DIVISION_OPTIONS = useMemo(() => {
    const seen = new Map();
    classAssignments.forEach((c) => {
      const value = `${c.grade}-${c.division}`;
      if (!seen.has(value)) {
        seen.set(value, {
          value,
          label: `${c.grade}${c.division}`,
          grade: c.grade,
          division: c.division,
        });
      }
    });
    return Array.from(seen.values());
  }, [classAssignments]);

  // Subjects available across ALL selected class-division combos
  const SUBJECT_OPTIONS = useMemo(() => {
    if (form.classDivision.length === 0) return [];
    const selectedPairs = form.classDivision.map((v) => v.split("-"));
    const matches = classAssignments.filter((c) =>
      selectedPairs.some(
        ([grade, division]) => c.grade === grade && c.division === division,
      ),
    );
    const subjects = matches.flatMap((m) => m.subject || []);
    return [...new Set(subjects)];
  }, [classAssignments, form.classDivision]);

  const handleSearch = (e) => {
    e?.preventDefault?.();
    setCurrentPage(1);
  };

  const handleAdd = () => {
    setFormError("");
    setFieldErrors({ classDivision: "", subject: "", heading: "", desc: "" });
    setShowForm(true);
  };

  const handleClassDivisionChange = (values) => {
    setForm((prev) => {
      const selectedPairs = values.map((v) => v.split("-"));
      const validSubjects = classAssignments
        .filter((c) =>
          selectedPairs.some(
            ([grade, division]) => c.grade === grade && c.division === division,
          ),
        )
        .flatMap((c) => c.subject || []);
      const nextSubject = prev.subject.filter((s) => validSubjects.includes(s));

      return { ...prev, classDivision: values, subject: nextSubject };
    });

    setFieldErrors((prev) => ({
      ...prev,
      classDivision: values.length > 0 ? "" : prev.classDivision,
    }));
  };

  const handleSubjectChange = (values) => {
    setForm((prev) => ({ ...prev, subject: values }));
    setFieldErrors((prev) => ({
      ...prev,
      subject: values.length > 0 ? "" : prev.subject,
    }));
  };

  const handleFormChange = (field) => (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, [field]: value }));

    // Clear that field's error as soon as it becomes valid
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const trimmed = value.trim();
      let stillInvalid = false;

      if (field === "heading") {
        stillInvalid =
          trimmed.length < HEADING_MIN || trimmed.length > HEADING_MAX;
      } else if (field === "desc") {
        stillInvalid = trimmed.length < DESC_MIN || trimmed.length > DESC_MAX;
      }

      return stillInvalid ? prev : { ...prev, [field]: "" };
    });
  };

  const validateForm = () => {
    const errors = {
      classDivision: "",
      subject: "",
      heading: "",
      desc: "",
    };

    if (form.classDivision.length === 0) {
      errors.classDivision = "Select at least one class.";
    }

    if (form.subject.length === 0) {
      errors.subject = "Select at least one subject.";
    }

    const trimmedHeading = form.heading.trim();
    if (!trimmedHeading) {
      errors.heading = "Title is required.";
    } else if (trimmedHeading.length < HEADING_MIN) {
      errors.heading = `Heading must be at least ${HEADING_MIN} characters.`;
    } else if (trimmedHeading.length > HEADING_MAX) {
      errors.heading = `Heading cannot exceed ${HEADING_MAX} characters.`;
    }

    const trimmedDesc = form.desc.trim();
    if (!trimmedDesc) {
      errors.desc = "Description is required.";
    } else if (trimmedDesc.length < DESC_MIN) {
      errors.desc = `Description must be at least ${DESC_MIN} characters.`;
    } else if (trimmedDesc.length > DESC_MAX) {
      errors.desc = `Description cannot exceed ${DESC_MAX} characters.`;
    }

    const isValid = Object.values(errors).every((msg) => !msg);
    return { errors, isValid };
  };

  const handleFormCancel = () => {
    setForm({
      classDivision: [],
      subject: [],
      heading: "",
      desc: "",
    });
    setFormError("");
    setFieldErrors({ classDivision: "", subject: "", heading: "", desc: "" });
    setShowForm(false);
  };

  const filteredNotices = useMemo(() => {
    if (!q.trim()) return notices;
    const query = q.trim().toLowerCase();
    return notices.filter((n) =>
      [n.title, n.subject]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [q, notices]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredNotices.length / CARDS_PER_PAGE),
  );
  const safePage = Math.min(currentPage, totalPages);

  const paginatedNotices = useMemo(() => {
    const start = (safePage - 1) * CARDS_PER_PAGE;
    return filteredNotices.slice(start, start + CARDS_PER_PAGE);
  }, [filteredNotices, safePage]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const pageRange = useMemo(
    () => getPageRange(safePage, totalPages, 1),
    [safePage, totalPages],
  );

  const rangeStart =
    filteredNotices.length === 0 ? 0 : (safePage - 1) * CARDS_PER_PAGE + 1;
  const rangeEnd = Math.min(safePage * CARDS_PER_PAGE, filteredNotices.length);

  return (
    <div className={tstyles.container}>
      <main className={tstyles.main}>
        <div className={nstyles.noticeRect} aria-label="Notice Board Panel">
          <div className={nstyles.noticeHeaderRow}>
            <div className={nstyles.headerLeftGroup}>
              <h2 className={nstyles.pageTitle}>Notice Board</h2>
            </div>
          </div>

          {/* Cards row */}
          <div className={nstyles.cardsRow}>
            {paginatedNotices.length === 0 ? (
              <p className={nstyles.emptyState}>No notices found.</p>
            ) : (
              paginatedNotices.map((n) => (
                <NoticeCard
                  key={n.id}
                  title={n.title}
                  meta={`Class ${n.className}${n.division} - ${n.subject}`}
                  desc={n.desc}
                  date={n.date}
                  image={n.image}
                  eventDate={n.eventDate}
                />
              ))
            )}
          </div>

          {/* Pagination */}
          {filteredNotices.length > 0 && (
            <div className={nstyles.paginationBar}>
              {
                totalPages > 1 &&    <span className={nstyles.paginationSummary}>
                Showing {rangeStart}–{rangeEnd} of {filteredNotices.length}{" "}
                notices
              </span>
              }
           
              {totalPages > 1 && (
                <nav
                  className={nstyles.pagination}
                  aria-label="Notice pagination"
                >
                  <button
                    className={nstyles.pageBtn}
                    onClick={() => goToPage(1)}
                    disabled={safePage === 1}
                    aria-label="First page"
                    title="First page"
                  >
                    «
                  </button>

                  <button
                    className={nstyles.pageBtn}
                    onClick={() => goToPage(safePage - 1)}
                    disabled={safePage === 1}
                    aria-label="Previous page"
                  >
                    Prev
                  </button>

                  {pageRange.map((page, idx) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className={nstyles.pageEllipsis}
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        className={`${nstyles.pageBtn} ${page === safePage ? nstyles.pageBtnActive : ""}`}
                        onClick={() => goToPage(page)}
                        aria-current={page === safePage ? "page" : undefined}
                      >
                        {page}
                      </button>
                    ),
                  )}

                  <button
                    className={nstyles.pageBtn}
                    onClick={() => goToPage(safePage + 1)}
                    disabled={safePage === totalPages}
                    aria-label="Next page"
                  >
                    Next
                  </button>

                  <button
                    className={nstyles.pageBtn}
                    onClick={() => goToPage(totalPages)}
                    disabled={safePage === totalPages}
                    aria-label="Last page"
                    title="Last page"
                  >
                    »
                  </button>
                </nav>
              )}
            </div>
          )}
        </div>
      </main>
      <Loader show={loading} />
      <ToastMessage
        show={toastState.show}
        onClose={() => setToastState({ show: false, message: "", type: "" })}
        message={toastState.message}
        type={toastState.type}
      />
    </div>
  );
}
